import ProposalApprovalConfirmDialog from "@/components/proposal/ProposalApprovalConfirmDialog";
import VariationOrderDocument from "@/components/proposals/VariationOrderDocument";
import VoApprovalSection from "@/components/proposals/VoApprovalSection";
import { isReasonablyValidEmail } from "@/lib/proposalCustomerActions";
import { trpc } from "@/lib/trpc";
import type { VoPaymentDetailsSnapshot } from "@shared/variationOrderPaymentDetails";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useParams } from "wouter";

export default function VariationOrderPublicPage() {
  const { token } = useParams<{ token: string }>();
  const utils = trpc.useUtils();
  const q = trpc.variationOrders.getPublic.useQuery(
    { token: token ?? "" },
    { enabled: Boolean(token) },
  );
  const approveMut = trpc.variationOrders.approvePublic.useMutation();
  const [approvalEmail, setApprovalEmail] = useState("");
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [localApproved, setLocalApproved] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState<string | null>(null);
  const [approvedAtLocal, setApprovedAtLocal] = useState<string | null>(null);
  const [emailDeliveryFailed, setEmailDeliveryFailed] = useState(false);

  useEffect(() => {
    if (q.data?.vo?.status === "approved") {
      setLocalApproved(true);
      setConfirmationEmail(q.data.vo.approvedEmail ?? null);
      setApprovedAtLocal(
        q.data.vo.approvedAt
          ? typeof q.data.vo.approvedAt === "string"
            ? q.data.vo.approvedAt
            : new Date(q.data.vo.approvedAt).toISOString()
          : null,
      );
    }
  }, [q.data?.vo?.status, q.data?.vo?.approvedAt, q.data?.vo?.approvedEmail]);

  if (q.isLoading) return <p className="p-8 text-sm text-muted-foreground">Loading variation order…</p>;
  if (q.error || !q.data) {
    return <p className="p-8 text-sm text-destructive">{q.error?.message ?? "Variation order not found"}</p>;
  }

  const { vo, lines } = q.data;
  const paymentDetails = (vo as { paymentDetails?: VoPaymentDetailsSnapshot | null }).paymentDetails ?? null;
  const allowsLegacySummary = Boolean(
    (vo as { allowsLegacySummary?: boolean }).allowsLegacySummary,
  );
  const systemOverviewChanges =
    (q.data as { systemOverviewChanges?: import("@shared/variationOrderSystemOverview").VoSystemOverviewChange[] })
      .systemOverviewChanges ?? [];
  const amountDue = Number(paymentDetails?.amountDueUponConfirmation ?? 0);
  const dateLabel = vo.sentAt
    ? new Date(vo.sentAt).toLocaleDateString("en-SG", { dateStyle: "medium" })
    : vo.createdAt
      ? new Date(vo.createdAt).toLocaleDateString("en-SG", { dateStyle: "medium" })
      : null;

  const isApproved = localApproved || vo.status === "approved";
  const displayStatus = isApproved ? "approved" : vo.status;
  const canAct = Boolean(vo.canAct) && vo.status === "sent" && !isApproved;
  const inactiveMessage =
    !canAct && !isApproved
      ? (vo as { inactiveMessage?: string | null }).inactiveMessage ??
        (vo.status === "draft"
          ? "This Variation Order has been updated by your consultant. The previous approval link is no longer valid."
          : vo.status === "cancelled"
            ? "This Variation Order has been cancelled and is no longer available for approval."
            : vo.status === "rejected"
              ? "This Variation Order was rejected."
              : null)
      : null;
  // Secure Link B only — resolved server-side from the DB, never derived from the numeric
  // proposalId on the client. Null when no active credential has been issued yet.
  const fullProposalHref = (vo as { fullProposalPath?: string | null }).fullProposalPath ?? null;
  const documentVersion = String(
    (vo as { documentVersion?: string | null }).documentVersion ??
      (vo as { updatedAt?: string | Date | null }).updatedAt ??
      "",
  ).trim();
  const displayApprovedAt = approvedAtLocal ?? vo.approvedAt ?? null;
  const displayConfirmationEmail =
    confirmationEmail?.trim() || String(vo.approvedEmail ?? "").trim() || null;

  const runApprove = () => {
    const email = approvalEmail.trim();
    if (!isReasonablyValidEmail(email)) {
      toast.error("Please enter a valid email address to continue.");
      return;
    }
    approveMut.mutate(
      {
        token: token!,
        approvalEmail: email,
        expectedUpdatedAt: documentVersion || undefined,
      },
      {
        onSuccess: (res) => {
          setLocalApproved(true);
          setApproveDialogOpen(false);
          setConfirmationEmail(res.confirmationEmailSentTo ?? email);
          setApprovedAtLocal(res.approvedAt ?? new Date().toISOString());
          setEmailDeliveryFailed(Boolean(res.emailDeliveryFailed));
          void utils.variationOrders.getPublic.invalidate({ token: token! });
          if (res.emailDeliveryFailed) {
            toast.success(
              "Variation Order approved. Confirmation email could not be sent — please contact your consultant if you need a copy.",
            );
          } else {
            toast.success("Thank you — your approval has been recorded.");
          }
        },
        onError: (e) => toast.error(e.message || "Failed to approve Variation Order."),
      },
    );
  };

  return (
    <>
      <VariationOrderDocument
        voNumber={vo.voNumber}
        proposalRef={vo.proposalRef}
        customerName={vo.customerName}
        dateLabel={dateLabel}
        status={displayStatus}
        lines={lines}
        originalContractValue={vo.originalContractValue}
        variationAmount={vo.variationAmount}
        revisedContractValue={vo.revisedContractValue}
        changeReason={vo.changeReason}
        paymentDetails={paymentDetails}
        allowsLegacySummary={allowsLegacySummary}
        fullProposalHref={fullProposalHref}
        systemOverviewChanges={systemOverviewChanges}
      >
        {inactiveMessage ? (
          <p
            className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950"
            data-testid="vo-public-inactive"
          >
            {inactiveMessage}
          </p>
        ) : null}

        <VoApprovalSection
          status={displayStatus}
          approvedAt={displayApprovedAt}
          approvedEmail={displayConfirmationEmail}
          emailDeliveryFailed={emailDeliveryFailed}
          approvePending={approveMut.isPending}
          onApproveClick={() => setApproveDialogOpen(true)}
        />
      </VariationOrderDocument>

      <ProposalApprovalConfirmDialog
        open={approveDialogOpen && canAct}
        onOpenChange={setApproveDialogOpen}
        variant="variation_order"
        proposalRef={String(vo.proposalRef ?? "")}
        variationOrderSummary={{
          voNumber: vo.voNumber,
          proposalRef: vo.proposalRef,
          amountDueUponConfirmation: amountDue,
          revisedContractValue: Number(vo.revisedContractValue ?? 0),
          variationAmount: Number(vo.variationAmount ?? 0),
        }}
        approvalEmail={approvalEmail}
        onApprovalEmailChange={setApprovalEmail}
        confirming={approveMut.isPending}
        confirmDisabled={isApproved}
        onConfirm={runApprove}
      />
    </>
  );
}
